import { toast } from '@hakimo-ui/hakimo/util';
import { EyeIcon, PaperClipIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useRef } from 'react';

interface Props {
  files: File[];
  onChange: (files: File[]) => void;
  onPreview: (file: File) => void;
}

function isPdf(file: File) {
  return (
    file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
  );
}

export function EmailAttachmentsField({ files, onChange, onPreview }: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files?.[0];
    if (picked && isPdf(picked)) {
      onChange([picked]);
    } else if (picked) {
      toast('Only PDF files can be attached', { type: 'error' });
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const remove = (index: number) => {
    onChange(files.filter((_, i) => i !== index));
  };

  return (
    <div>
      <span className="dark:text-dark-secondary-text mb-1 block text-sm text-gray-700">
        Attachments
      </span>

      {files.length === 0 && (
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="dark:bg-dark-bg dark:border-dark-border-surface dark:text-dark-text inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700"
            onClick={() => fileInputRef.current?.click()}
          >
            <PaperClipIcon className="h-4 w-4" />
            Attach File
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf,.pdf"
            className="hidden"
            onChange={onPick}
          />
        </div>
      )}

      {files.length > 0 && (
        <div className="mt-2 space-y-1">
          {files.map((file, index) => (
            <div
              key={index}
              className="dark:bg-dark-bg dark:border-dark-border-surface flex items-center justify-between rounded-md border border-gray-200 bg-gray-50 px-3 py-1.5 text-sm"
            >
              <button
                type="button"
                onClick={() => onPreview(file)}
                title="Preview attachment"
                className="flex items-center gap-1.5 truncate text-blue-600 hover:underline dark:text-blue-400"
              >
                <EyeIcon className="h-4 w-4 shrink-0" />
                <span className="truncate">{file.name}</span>
              </button>
              <button
                type="button"
                onClick={() => remove(index)}
                aria-label={`Remove ${file.name}`}
                className="ml-2 shrink-0 text-gray-400 hover:text-red-500"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default EmailAttachmentsField;
